import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <>
      <SEOHead
        title="Privacy Policy - Smartborn"
        description="Privacy Policy for Smartborn - Learn how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, personal information, smartborn privacy"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          <div className="max-w-4xl mx-auto">
            <h1 className="text-h1 mb-8">Privacy Policy</h1>
            
            <div className="prose prose-lg max-w-none space-y-8">
              <section>
                <p className="text-muted-foreground mb-6">
                  Last Updated: November 2024
                </p>
                <p className="text-body leading-relaxed">
                  At Smartborn, we are committed to protecting your privacy and ensuring the security of your personal information. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Information We Collect</h2>
                <h3 className="text-h3 mb-3">Personal Information</h3>
                <p className="text-body leading-relaxed mb-4">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-body">
                  <li>Fill out contact forms or request information</li>
                  <li>Subscribe to our newsletter or communications</li>
                  <li>Register for events or webinars</li>
                  <li>Apply for job positions</li>
                  <li>Interact with our services</li>
                </ul>
                <p className="text-body leading-relaxed mt-4">
                  This information may include: name, email address, phone number, company name, job title, and any other information you choose to provide.
                </p>
              </section>

              <section>
                <h3 className="text-h3 mb-3">Automatically Collected Information</h3>
                <p className="text-body leading-relaxed mb-4">
                  When you visit our website, we automatically collect certain information about your device, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-body">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Referring URLs</li>
                  <li>Pages viewed and time spent on pages</li>
                  <li>Device identifiers</li>
                </ul>
              </section>

              <section>
                <h2 className="text-h2 mb-4">How We Use Your Information</h2>
                <p className="text-body leading-relaxed mb-4">
                  We use the information we collect for various purposes, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-body">
                  <li>Providing and maintaining our services</li>
                  <li>Responding to your inquiries and requests</li>
                  <li>Sending you newsletters and marketing communications (with your consent)</li>
                  <li>Improving our website and services</li>
                  <li>Analyzing usage trends and preferences</li>
                  <li>Detecting and preventing fraud or security issues</li>
                  <li>Complying with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Cookies and Tracking Technologies</h2>
                <p className="text-body leading-relaxed mb-4">
                  We use cookies and similar tracking technologies to track activity on our website and hold certain information. 
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. 
                  For more information, please see our <Link to="/cookie-policy" className="text-primary hover:text-primary/80">Cookie Policy</Link>.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Data Sharing and Disclosure</h2>
                <p className="text-body leading-relaxed mb-4">
                  We do not sell your personal information. We may share your information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-body">
                  <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf</li>
                  <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong>With Your Consent:</strong> When you have given us explicit permission</li>
                </ul>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Data Security</h2>
                <p className="text-body leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information. 
                  However, no method of transmission over the Internet or electronic storage is 100% secure, 
                  and we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Your Rights</h2>
                <p className="text-body leading-relaxed mb-4">
                  Depending on your location, you may have certain rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-body">
                  <li>Access to your personal information</li>
                  <li>Correction of inaccurate information</li>
                  <li>Deletion of your information</li>
                  <li>Objection to processing</li>
                  <li>Data portability</li>
                  <li>Withdrawal of consent</li>
                </ul>
                <p className="text-body leading-relaxed mt-4">
                  To exercise these rights, please contact us at contact@smartborn.io
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Children's Privacy</h2>
                <p className="text-body leading-relaxed">
                  Our services are not directed to children under the age of 13. We do not knowingly collect personal information 
                  from children under 13. If you become aware that a child has provided us with personal information, 
                  please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">International Data Transfers</h2>
                <p className="text-body leading-relaxed">
                  Your information may be transferred to and processed in countries other than your country of residence. 
                  These countries may have data protection laws that are different from the laws of your country. 
                  We take appropriate measures to ensure your information receives adequate protection.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Changes to This Privacy Policy</h2>
                <p className="text-body leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
                  Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy 
                  periodically for any changes.
                </p>
              </section>

              <section>
                <h2 className="text-h2 mb-4">Contact Us</h2>
                <p className="text-body leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-muted/30 p-6 rounded-lg space-y-2">
                  <p className="text-body"><strong>Email:</strong> contact@smartborn.io</p>
                  <p className="text-body"><strong>Phone:</strong> +91 98869 28201</p>
                  <p className="text-body"><strong>Address:</strong> Bangalore, India</p>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;